// ©2024 Austin App House. All rights reserved.
import React from 'react';
import { connect } from 'react-redux';
import { toggleModal } from '../../storage/actions/modalsActions';
import { generateToken } from '../../storage/actions/superAdmin';
import { reloadData } from '../../storage/actions/applicationStateActions';
import DashboardIcon from '../dashboard/icons/DashboardIcon';
import Notification from '../notification';
import {
  updateCompanySuspension,
  isCompanySuspended,
  getCompanyId,
} from '../../utils/api/adminCompanies';

const prevent = (handler) => (event) => {
  event.preventDefault();
  if (handler) handler(event);
};

const CompanyActions = ({
  toggleModal, item, generateToken, reloadData: reloadAppData,
}) => {
  const suspended = isCompanySuspended(item);
  const companyId = getCompanyId(item);
  const companyName = item.name || 'this client account';

  const handlerEditCompany = () => {
    toggleModal(true, 'editCompanyBySuperAdmin', false, item);
  };

  const runSuspension = async (nextSuspended) => {
    try {
      const result = await updateCompanySuspension(companyId, nextSuspended);
      Notification('success', {
        message: result?.message
          || `Client account ${nextSuspended ? 'suspended' : 'unsuspended'} successfully.`,
      });
      reloadAppData();
    } catch (error) {
      Notification('error', {
        message: error.response?.data?.message || error.message,
      });
      throw error;
    }
  };

  const handleToggleSuspension = () => {
    if (suspended) {
      toggleModal(true, 'confirmActionModal', false, {
        title: 'Unsuspend account',
        message: `Please confirm that you want to unsuspend ${companyName}. All users on this account will be able to log in again.`,
        confirmLabel: 'Unsuspend',
        cancelLabel: 'Cancel',
        onConfirm: () => runSuspension(false),
      });
    } else {
      toggleModal(true, 'confirmActionModal', false, {
        title: 'Suspend account',
        message: `Please confirm that you want to fully suspend ${companyName}. All users on this account will be blocked from logging in.`,
        confirmLabel: 'Suspend',
        cancelLabel: 'Cancel',
        onConfirm: () => runSuspension(true),
      });
    }
  };

  const generateAuthKey = () => {
    generateToken(item.creatorEmail);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(item.authToken);
      Notification('success', {
        message: 'Copied to clipboard!',
      });
    } catch (error) {
      console.error('Unable to copy item to clipboard:', error);
    }
  };

  return (
    <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
      <button
        type="button"
        className={`row-action-btn${suspended ? ' row-action-btn--restore' : ' row-action-btn--suspend'}`}
        data-tooltip={suspended ? 'UNSUSPEND ACCOUNT' : 'SUSPEND ACCOUNT'}
        aria-label={suspended ? 'Unsuspend account' : 'Suspend account'}
        onClick={prevent(handleToggleSuspension)}
      >
        <DashboardIcon name="ban" size={14} strokeWidth="2" />
      </button>
      <button
        type="button"
        className="row-action-btn"
        title="Edit company"
        aria-label="Edit company"
        onClick={prevent(handlerEditCompany)}
      >
        <DashboardIcon name="edit" size={14} strokeWidth="2" />
      </button>
      <button
        type="button"
        className="row-action-btn"
        title="Generate auth key"
        aria-label="Generate auth key"
        onClick={prevent(generateAuthKey)}
      >
        <DashboardIcon name="plus" size={14} strokeWidth="2" />
      </button>
      <button
        type="button"
        className="row-action-btn"
        title="Copy auth token"
        aria-label="Copy auth token"
        onClick={prevent(copyToClipboard)}
      >
        <DashboardIcon name="duplicate" size={14} strokeWidth="2" />
      </button>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  generateToken,
  reloadData,
};

export default connect(null, mapDispatchToProps)(CompanyActions);
